<template>
    <modal
        v-if="show"
        class="complianceModal"
        :size="'is-middle'"
        :title="'Add Number(s) to Toll-Free Profile'"
        :form="form"
        :submitText="'Save'"
        :cancelText="'Cancel'"
        :submitClasses="{
            'is-primary': true,
            'is-busy': form.busy,
        }"
        :submitDisabled="submitDisabled"
        @closed="handleClosed"
        @submit="handleSubmit"
    >
        <div v-if="loading" class="complianceModal__content">
            <basic-loader :center="true" aria-label="TollFreeAddNumbersModal" />
        </div>
        <div v-else class="complianceModal__content">
            <template>
                <div class="complianceModal__title">Toll-Free Numbers List</div>

                <div class="complianceModal__column">
                    <b-table
                        v-if="item.numbers.length > 0"
                        :data="item.numbers"
                        class="tollFreeTable"
                    >
                        <template #default="props">
                            <b-table-column label="Number" field="number">
                                <div>{{ props.row.formatted_number }}</div>
                            </b-table-column>

                            <b-table-column label="Inbox" field="inbox">
                                <div>
                                    {{ props.row.numberable ? props.row.numberable.name : '-' }}
                                </div>
                            </b-table-column>

                            <b-table-column label="Status" field="inbox">
                                <div class="statusColumn">
                                    <fa-icon
                                        :icon="getIcon(props.row.verified_status)"
                                        :style="getIconStyle(props.row.verified_status)"
                                    />
                                    <span>
                                        {{ props.row.verified_status }}
                                    </span>
                                </div>
                            </b-table-column>
                        </template>
                    </b-table>
                </div>

                <div class="complianceModal__title">Toll-Free Numbers</div>

                <div class="complianceModal__column">
                    <form-dropdown
                        id="dropdown-numbers"
                        ref="dropdown"
                        :settings="dropdownSettingsNumbers"
                        label="Select unverified Toll-Free numbers you want to add to this list"
                        :tooltip="'All the numbers you include in this list will be verified under this use case profile'"
                        placeholder="Please Select"
                        :hideLabelColon="true"
                        :hideRequired="true"
                        @input="updateNumbers"
                    />
                </div>
            </template>
        </div>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';

export default {
    props: {
        show: { type: Boolean, required: true },
        item: { type: Object, required: true },
    },

    emits: ['closed', 'updated'],

    data() {
        return {
            loading: true,
            form: new Form(
                {
                    numbers: [],
                },
                false,
                false
            ),
            existNumbers: '',
        };
    },

    computed: {
        submitDisabled() {
            return Boolean(!this.form.numbers.length);
        },

        dropdownSettingsNumbers() {
            const options = this.numbers
                .filter((item) => item.isTollFree && item.statusUnverified && !item.has_profile)
                .filter((item) => !this.item.idsNumbers.includes(item.id))
                .map((item) => ({
                    id: item.id,
                    number: item.formatted_number,
                    inboxName: item.inbox.name,
                    group: 'main',
                }));

            return {
                valueField: 'id',
                searchField: ['label'],
                options: options,
                highlight: true,
                dropdownClass: 'selectize-dropdown selectize-dropdown-column',
                render: {
                    item: this.dropdownNumberItem,
                    option: this.dropdownNumberOption,
                    optgroup_header: this.dropdownNumberOptGroupHeader,
                },
                maxItems: null,
                dropdownParent: 'body',
                plugins: ['remove_button'],
                optgroups: [{ value: 'main' }],
                optgroupField: 'group',
            };
        },

        show() {
            return this.showTollFreeAddNumbersModal;
        },

        item() {
            return this.showTollFreeAddNumbersModalItem;
        },

        ...mapState(stores.Number, ['numbers']),
        ...mapState(stores.TollFreeStore, [
            'showTollFreeAddNumbersModal',
            'showTollFreeAddNumbersModalItem',
        ]),
    },

    watch: {
        show(value) {
            if (value) {
                this.init();
            }
        },
    },

    methods: {
        async init() {
            this.loading = true;

            this.reset();

            await Promise.all([this.initNumbers()]);

            this.initForm();

            this.loading = false;
        },

        initForm() {
            this.form.numbers = this.item.numbers.map((item) => item.id);
            this.existNumbers = this.item.textNumbers;
        },

        async initNumbers() {
            try {
                await this.$store.dispatch('getNumbers');
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'initNumbers');
            }
        },

        reset() {
            this.form.reset();
        },

        updateNumbers(data) {
            this.form.numbers = data;
        },

        async handleSubmit() {
            try {
                this.form.numbers = [
                    ...this.item.numbers.map((item) => item.id),
                    ...this.form.numbers,
                ];

                await this.$store.dispatch('updateTollFreeProfile', {
                    form: this.form,
                    id: this.item.id,
                });
            } catch (e) {
                this.form.busy = false;

                this.$log.error(e, this.$options.__file, 'handleSubmit');
            }
        },

        handleClosed() {
            this.$store.dispatch('handleShowTollFreeAddNumbersModal', {
                status: false,
                item: null,
            });
            this.$emit('closed');
        },

        dropdownNumberItem(item, escape) {
            return `<div class="selectize-contact tag is-small is-blue">
                        <strong>${escape(item.number)}</strong>: ${escape(item.inboxName)}
                    </div>`;
        },

        dropdownNumberOption(item, escape) {
            return `<div class="selectize-option__columns">
                        <div class="selectize-option__column">
                            <span>
                                ${escape(item.number)}
                            </span>
                        </div>
                        <div class="selectize-option__column">
                            <span>
                                ${escape(item.inboxName)}
                            </span>
                        </div>
                    </div>`;
        },

        dropdownNumberOptGroupHeader(item, escape) {
            return `<div class="selectize-option__columns selectize-option__columns--header">
                        <div class="selectize-option__column">
                            <span>
                                Number
                            </span>
                        </div>
                        <div class="selectize-option__column">
                            <span>
                                Inbox
                            </span>
                        </div>
                    </div>`;
        },

        getIcon(status) {
            if (status === 'Under Review') {
                return 'clock';
            }
            if (status === 'Verified') {
                return 'check-circle';
            }

            return 'times-circle';
        },

        getIconStyle(status) {
            if (status === 'Under Review') {
                return { color: '#7A7A7A' };
            }
            if (status === 'Verified') {
                return { color: '#13B763' };
            }

            return { color: '#DA5350' };
        },

        onClose() {},
    },
};
</script>
